import { inject, Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '@environments/environment';
import { AudioCallTelemetryService } from '@app/services/audio-call-telemetry.service';
import { audioCallSafeMetrics } from '@app/services/audio-call-telemetry.interface';
import { PlatformService } from '@services/platform.service';
import { AppInfoService } from '@services/app-info.service';
import {
  AudioCallReliableEventName,
  AudioCallReliableSendResult,
  SOCKET_EVENT_NAMES,
} from './web-socket.interfaces';

export type AudioCallConnectionDebugTone = 'neutral' | 'success' | 'warning' | 'error';
export type AudioCallConnectionDebugEventDirection = 'incoming' | 'outgoing' | 'local' | 'system';

export interface AudioCallConnectionDebugParams {
  [key: string]: string | number;
}

export interface AudioCallConnectionDebugStatus {
  actionKey: string;
  eventKey?: string;
  params?: AudioCallConnectionDebugParams;
  tone: AudioCallConnectionDebugTone;
}

export interface AudioCallConnectionDebugEntry extends AudioCallConnectionDebugStatus {
  source: string;
  stage: string;
  methodName?: string;
  eventName?: string;
  eventDirection?: AudioCallConnectionDebugEventDirection;
  timestamp: number;
  detail?: unknown;
}

interface AudioCallConnectionDebugTrackOptions {
  detail?: unknown;
  eventDirection?: AudioCallConnectionDebugEventDirection;
  eventName?: string;
  methodName?: string;
  updateCurrentStatus?: boolean;
}

interface AudioCallConnectionDebugSessionContext {
  orderId: number | null;
  role: 'customer' | 'executor' | null;
}

interface AudioCallConnectionDebugMappedStatus {
  status: AudioCallConnectionDebugStatus;
  updateCurrentStatus: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AudioCallConnectionDebugService {
  private readonly telemetry = inject(AudioCallTelemetryService);
  private readonly ngZone = inject(NgZone);
  private readonly platform = inject(PlatformService);
  private readonly appInfo = inject(AppInfoService);
  public readonly currentStatus$: Observable<AudioCallConnectionDebugStatus | null>;
  public readonly currentEntry$: Observable<AudioCallConnectionDebugEntry | null>;
  public readonly entries$: Observable<AudioCallConnectionDebugEntry[]>;

  private readonly currentStatusSubject: BehaviorSubject<AudioCallConnectionDebugStatus | null> =
    new BehaviorSubject<AudioCallConnectionDebugStatus | null>(null);
  private readonly currentEntrySubject: BehaviorSubject<AudioCallConnectionDebugEntry | null> =
    new BehaviorSubject<AudioCallConnectionDebugEntry | null>(null);
  private readonly entriesSubject: BehaviorSubject<AudioCallConnectionDebugEntry[]> =
    new BehaviorSubject<AudioCallConnectionDebugEntry[]>([]);
  private readonly debugStackFrameSkipNames: string[] = [
    'extractStackFrameName',
    'logAudioCallReliableStep',
    'mapReliableStepToStatus',
    'normalizeMethodName',
    'resolveCallerMethodName',
    'resolveConnectionDebugEventLabel',
    'shouldSkipDebugStackFrame',
    'trackReliableReceiveStatus',
    'traceIncomingReliableEvent',
    'traceReliableResult',
    'traceReliableStep',
    'track',
    'trackConnectionDebugStatus',
    'trackDebugStatus',
  ];
  private readonly maxEntries: number = 250;
  private active: boolean = false;
  private sessionId: string = '';
  private startedAt: number = 0;
  private sequence: number = 0;
  private readonly recentStages = new Map<string, number>();
  private sessionContext: AudioCallConnectionDebugSessionContext = {
    orderId: null,
    role: null,
  };

  constructor() {
    this.currentStatus$ = this.currentStatusSubject.asObservable();
    this.currentEntry$ = this.currentEntrySubject.asObservable();
    this.entries$ = this.entriesSubject.asObservable();
  }

  public activate(orderId?: number | null, role?: 'customer' | 'executor' | null): void {
    if (this.active && (this.sessionContext.orderId === orderId || this.sessionContext.orderId === null)) {
      this.setOrderContext(orderId, role);
      return;
    }
    this.active = true;
    this.startedAt = Date.now();
    // Correlation only, not an authentication secret; works in older WebViews too.
    this.sessionId = `${this.startedAt.toString(36)}-${Math.random().toString(36).slice(2)}`;
    this.sequence = 0;
    this.recentStages.clear();
    this.sessionContext = {
      orderId: typeof orderId === 'number' && Number.isFinite(orderId) ? orderId : null,
      role: role ?? null,
    };
    this.entriesSubject.next([]);
    this.currentStatusSubject.next(null);
    this.currentEntrySubject.next(null);
    void this.recordClientMetadata(this.sessionId);
  }

  public begin(role: 'customer' | 'executor', orderId: number | null = null): string {
    this.deactivate();
    this.activate(orderId, role);
    return this.sessionId;
  }

  public finishBeforeOrder(
    sessionId: string | null,
    stage: 'ui.socket_unavailable' | 'ui.socket_init_timeout' | 'ui.start_cancelled'
      | 'ui.microphone_denied' | 'ui.order_create_failed',
  ): void {
    // A delayed precheck must not finish a newer attempt or an established order.
    if (!this.active || sessionId !== this.sessionId || this.sessionContext.orderId !== null) return;
    this.milestone(stage, undefined, 'warning');
    this.deactivate();
  }

  public deactivate(): void {
    this.milestone('session.finished');
    this.active = false;
    this.sessionContext = {
      orderId: null,
      role: null,
    };
    this.entriesSubject.next([]);
    this.currentStatusSubject.next(null);
    this.currentEntrySubject.next(null);
  }

  public setOrderContext(orderId?: number | null, role?: 'customer' | 'executor' | null): void {
    if (this.active && this.sessionContext.orderId !== null && orderId && orderId !== this.sessionContext.orderId) {
      this.deactivate();
      this.activate(orderId, role);
      return;
    }
    this.sessionContext = {
      orderId: typeof orderId === 'number' && Number.isFinite(orderId) ? orderId : this.sessionContext.orderId,
      role: role ?? this.sessionContext.role,
    };
  }

  public milestone(stage: string, detail?: unknown, tone: AudioCallConnectionDebugTone = 'neutral'): void {
    this.track('call', stage, {actionKey: 'call.audioCall.debugStatus.processing', tone}, {
      detail, updateCurrentStatus: false,
    });
  }

  public track(
    source: string,
    stage: string,
    status: AudioCallConnectionDebugStatus,
    options?: AudioCallConnectionDebugTrackOptions,
  ): void {
    if (!this.active) return;
    const resolvedMethodName: string | undefined = options?.methodName
      ?? (environment.production ? undefined : this.resolveCallerMethodName());

    const entry: AudioCallConnectionDebugEntry = {
      ...status,
      source,
      stage,
      ...(resolvedMethodName ? {methodName: resolvedMethodName} : {}),
      ...(options?.eventName ? {eventName: options.eventName} : {}),
      ...(options?.eventDirection ? {eventDirection: options.eventDirection} : {}),
      timestamp: Date.now(),
      ...(options?.detail !== undefined ? {detail: audioCallSafeMetrics(options.detail)} : {}),
    };
    const nextEntries: AudioCallConnectionDebugEntry[] = [
      ...this.entriesSubject.value,
      entry,
    ];
    this.entriesSubject.next(nextEntries.slice(-this.maxEntries));

    if (options?.updateCurrentStatus !== false) {
      this.currentStatusSubject.next(status);
      this.currentEntrySubject.next(entry);
    }

    this.recordTelemetry(entry);
    if (!environment.production) console.info('[AudioCallDebug]', {
      source: entry.source,
      stage: entry.stage,
      methodName: entry.methodName ?? null,
      eventName: entry.eventName ?? null,
      eventDirection: entry.eventDirection ?? null,
      actionKey: entry.actionKey,
      eventKey: entry.eventKey ?? null,
      params: entry.params ?? null,
      tone: entry.tone,
      detail: entry.detail ?? null,
      orderId: this.sessionContext.orderId,
      role: this.sessionContext.role,
      timestamp: entry.timestamp,
    });
  }

  public traceReliableStep(
    eventName: AudioCallReliableEventName,
    step: string,
    payload: { [key: string]: unknown },
  ): void {
    if (!this.isCurrentOrder(payload['order_id'])) return;
    const mappedStatus: AudioCallConnectionDebugMappedStatus | null =
      this.mapReliableStepToStatus(eventName, step, payload);
    if (!mappedStatus) return;

    this.track(
      'socket',
      `reliable.${eventName}.${step}`,
      mappedStatus.status,
      {
        detail: payload,
        eventDirection: 'outgoing',
        eventName: eventName,
        updateCurrentStatus: mappedStatus.updateCurrentStatus,
      },
    );
  }

  public traceReliableResult(eventName: AudioCallReliableEventName, result: AudioCallReliableSendResult, orderId?: number): void {
    if (!this.isCurrentOrder(orderId ?? result.receipt?.order_id)) return;
    if (result.status === 'cancelled') return;
    const eventKey: string | undefined = this.resolveEventKey(eventName);
    if (!eventKey) return;
    if (result.delivered) {
      this.track(
        'socket',
        `reliable.${eventName}.confirmed`,
        {
          actionKey: 'call.audioCall.debugStatus.confirmed',
          eventKey,
          tone: 'success',
        },
        {
          detail: result,
          eventDirection: 'outgoing',
          eventName: eventName,
        },
      );
      return;
    }

    const statusValue: string = typeof result?.status === 'string' ? result.status : '';
    if (statusValue === 'socket_not_ready') {
      this.track(
        'socket',
        `reliable.${eventName}.socket_not_ready`,
        {
          actionKey: 'call.audioCall.debugStatus.socketNotReady',
          eventKey,
          tone: 'warning',
        },
        {
          detail: result,
          eventDirection: 'outgoing',
          eventName: eventName,
        },
      );
      return;
    }

    if (statusValue === 'ack_timeout') {
      this.track(
        'socket',
        `reliable.${eventName}.ack_timeout`,
        {
          actionKey: 'call.audioCall.debugStatus.waitingAck',
          eventKey,
          tone: 'warning',
        },
        {
          detail: result,
          eventDirection: 'outgoing',
          eventName: eventName,
        },
      );
      return;
    }

    if (statusValue === 'receipt_timeout') {
      this.track(
        'socket',
        `reliable.${eventName}.receipt_timeout`,
        {
          actionKey: 'call.audioCall.debugStatus.waitingReceipt',
          eventKey,
          tone: 'warning',
        },
        {
          detail: result,
          eventDirection: 'outgoing',
          eventName: eventName,
        },
      );
      return;
    }

    this.track(
      'socket',
      `reliable.${eventName}.failed`,
      {
        actionKey: 'call.audioCall.debugStatus.failed',
        eventKey,
        tone: 'error',
      },
      {
        detail: result,
        eventDirection: 'outgoing',
        eventName: eventName,
      },
    );
  }

  public traceIncomingReliableEvent(eventName: AudioCallReliableEventName, payload: { [key: string]: unknown }): void {
    if (!this.isCurrentOrder(payload['order_id'])) return;
    const eventKey: string | undefined = this.resolveEventKey(eventName);
    if (!eventKey) return;

    this.track(
      'socket',
      `incoming.${eventName}`,
      {
        actionKey: 'call.audioCall.debugStatus.received',
        eventKey,
        tone: 'neutral',
      },
      {
        detail: payload,
        eventDirection: 'incoming',
        eventName: eventName,
        updateCurrentStatus: eventName !== SOCKET_EVENT_NAMES.AUDIO_CALL_CANDIDATE,
      },
    );
  }

  private isCurrentOrder(orderId: unknown): boolean {
    return typeof orderId !== 'number' || this.sessionContext.orderId === null
      || orderId === this.sessionContext.orderId;
  }

  private async recordClientMetadata(sessionId: string): Promise<void> {
    try {
      const version = await this.appInfo.getShortAppVersion();
      if (!this.active || this.sessionId !== sessionId) return;
      const platform = this.platform.isAndroidApp ? 'android_app' : this.platform.isIosApp ? 'ios_app'
        : this.platform.isAndroidWeb ? 'android_web' : this.platform.isIosWeb ? 'ios_web' : 'web';
      this.milestone('session.client', {client_platform: platform, app_version: version});
    } catch {
      // Native metadata is optional and never blocks startup.
    }
  }

  private recordTelemetry(entry: AudioCallConnectionDebugEntry): void {
    if (entry.stage.startsWith('candidate.') || entry.stage.includes('.candidate.') || entry.stage.includes('audioCallCandidate')) return;
    const now = entry.timestamp;
    const metrics = audioCallSafeMetrics(entry.detail);
    const state = metrics['iceState'] ?? metrics['connectionState'] ?? '';
    const key = `${entry.source}:${entry.stage}:${state}`;
    const previous = this.recentStages.get(key);
    if (previous !== undefined && now - previous < 1000) return;
    if (this.recentStages.size >= 200) this.recentStages.clear();
    this.recentStages.set(key, now);
    this.sequence += 1;
    try {
      this.ngZone.runOutsideAngular((): void => this.telemetry.record({
        order_id: this.sessionContext.orderId,
        session_id: this.sessionId, seq: this.sequence, timestamp: now,
        elapsed_ms: Math.max(0, now - this.startedAt), role: this.sessionContext.role,
        source: entry.source, stage: entry.stage, tone: entry.tone,
      }, metrics));
    } catch {
      // Diagnostics must not interrupt signaling or media cleanup.
    }
  }

  private resolveCallerMethodName(): string | undefined {
    const error: Error = new Error();
    if (typeof error.stack !== 'string' || !error.stack) {
      return undefined;
    }

    const stackLines: string[] = error.stack
      .split('\n')
      .map((line: string): string => line.trim())
      .filter((line: string): boolean => line.length > 0);

    for (const stackLine of stackLines) {
      const extractedName: string | null = this.extractStackFrameName(stackLine);
      if (!extractedName) continue;

      const normalizedName: string = this.normalizeMethodName(extractedName);
      if (!normalizedName || this.shouldSkipDebugStackFrame(normalizedName)) {
        continue;
      }

      return normalizedName;
    }

    return undefined;
  }

  private extractStackFrameName(stackLine: string): string | null {
    if (stackLine === 'Error') return null;

    const chromeMatch: RegExpMatchArray | null = stackLine.match(/^at\s+(.+?)\s+\(/);
    if (chromeMatch?.[1]) {
      return chromeMatch[1];
    }

    const safariMatch: RegExpMatchArray | null = stackLine.match(/^([^@]+)@/);
    if (safariMatch?.[1]) {
      return safariMatch[1];
    }

    const chromeNoParenMatch: RegExpMatchArray | null = stackLine.match(/^at\s+(.+)$/);
    if (chromeNoParenMatch?.[1] && !chromeNoParenMatch[1].includes('/')) {
      return chromeNoParenMatch[1];
    }

    return null;
  }

  private normalizeMethodName(methodName: string): string {
    return methodName
      .trim()
      .replace(/^async\s+/, '')
      .replace(/^new\s+/, '')
      .replace(/^Object\./, '')
      .replace(/^Generator\./, '');
  }

  private shouldSkipDebugStackFrame(methodName: string): boolean {
    const shortMethodName: string = methodName.split('.').pop() ?? methodName;
    if (!shortMethodName) return true;

    return this.debugStackFrameSkipNames.includes(shortMethodName);
  }

  private mapReliableStepToStatus(
    eventName: AudioCallReliableEventName,
    step: string,
    payload: { [key: string]: unknown },
  ): AudioCallConnectionDebugMappedStatus | null {
    const eventKey: string | undefined = this.resolveEventKey(eventName);
    if (!eventKey) return null;

    const updateCurrentStatus: boolean = eventName !== SOCKET_EVENT_NAMES.AUDIO_CALL_CANDIDATE;
    const attempt: number | null = this.normalizeNumber(payload?.attempt) ?? null;
    const maxAttempts: number | null = this.normalizeNumber(payload?.maxAttempts) ?? null;

    if (step === 'send_attempt') {
      return {
        status: {
          actionKey: 'call.audioCall.debugStatus.sending',
          eventKey,
          ...(attempt && maxAttempts ? {params: {attempt, maxAttempts}} : {}),
          tone: 'neutral',
        },
        updateCurrentStatus,
      };
    }

    if (step === 'send_blocked_socket_not_ready') {
      return {
        status: {
          actionKey: 'call.audioCall.debugStatus.socketNotReady',
          eventKey,
          tone: 'warning',
        },
        updateCurrentStatus,
      };
    }

    if (step === 'ack_result') {
      const ackReceived: boolean = payload?.ack_received === true;
      const ackDelivered: boolean = payload?.ack_delivered === true;
      const ackRetryable: boolean = payload?.ack_retryable === true;

      if (!ackReceived) {
        return {
          status: {
            actionKey: 'call.audioCall.debugStatus.waitingAck',
            eventKey,
            tone: 'warning',
          },
          updateCurrentStatus,
        };
      }

      if (ackDelivered) {
        return {
          status: {
            actionKey: 'call.audioCall.debugStatus.waitingReceipt',
            eventKey,
            tone: 'neutral',
          },
          updateCurrentStatus,
        };
      }

      if (ackRetryable) {
        return {
          status: {
            actionKey: 'call.audioCall.debugStatus.retrying',
            eventKey,
            ...(attempt && maxAttempts ? {params: {attempt, maxAttempts}} : {}),
            tone: 'warning',
          },
          updateCurrentStatus,
        };
      }

      return {
        status: {
          actionKey: 'call.audioCall.debugStatus.failed',
          eventKey,
          tone: 'error',
        },
        updateCurrentStatus,
      };
    }

    if (step === 'receipt_result') {
      const receiptReceived: boolean = payload?.receipt_received === true;
      const receiptStatus: unknown = payload?.receipt_status;

      if (!receiptReceived) {
        return {
          status: {
            actionKey: 'call.audioCall.debugStatus.waitingReceipt',
            eventKey,
            tone: 'warning',
          },
          updateCurrentStatus,
        };
      }

      if (receiptStatus === 'processed' || receiptStatus === 'duplicate') {
        return {
          status: {
            actionKey: 'call.audioCall.debugStatus.confirmed',
            eventKey,
            tone: 'success',
          },
          updateCurrentStatus,
        };
      }

      if (receiptStatus === 'ignored') {
        return {
          status: {
            actionKey: 'call.audioCall.debugStatus.ignored',
            eventKey,
            tone: 'warning',
          },
          updateCurrentStatus,
        };
      }

      if (receiptStatus === 'failed') {
        return {
          status: {
            actionKey: 'call.audioCall.debugStatus.failed',
            eventKey,
            tone: 'error',
          },
          updateCurrentStatus,
        };
      }
    }

    return null;
  }

  private resolveEventKey(eventName: AudioCallReliableEventName): string | undefined {
    if (eventName === SOCKET_EVENT_NAMES.AUDIO_CALL_RESPONSE) {
      return 'call.audioCall.debugEvents.response';
    }
    if (eventName === SOCKET_EVENT_NAMES.AUDIO_CALL_CUSTOMER_READY) {
      return 'call.audioCall.debugEvents.customerReady';
    }
    if (eventName === SOCKET_EVENT_NAMES.AUDIO_CALL_OFFER) {
      return 'call.audioCall.debugEvents.offer';
    }
    if (eventName === SOCKET_EVENT_NAMES.AUDIO_CALL_ANSWER) {
      return 'call.audioCall.debugEvents.answer';
    }
    if (eventName === SOCKET_EVENT_NAMES.AUDIO_CALL_CANDIDATE) {
      return 'call.audioCall.debugEvents.candidate';
    }
    if (eventName === SOCKET_EVENT_NAMES.AUDIO_CALL_EXIT) {
      return 'call.audioCall.debugEvents.exit';
    }
    if (eventName === SOCKET_EVENT_NAMES.AUDIO_CALL_EXIT_FROM_BACKEND) {
      return 'call.audioCall.debugEvents.exitFromBackend';
    }
    if (eventName === SOCKET_EVENT_NAMES.AUDIO_CALL_PARTNER_CONNECTED_STATUS) {
      return 'call.audioCall.debugEvents.partnerStatus';
    }
    if (eventName === SOCKET_EVENT_NAMES.AUDIO_CALL_REQUEST_RECONNECT) {
      return 'call.audioCall.debugEvents.requestReconnect';
    }
    if (eventName === SOCKET_EVENT_NAMES.AUDIO_CALL_CUSTOMER_READY_RECONNECT) {
      return 'call.audioCall.debugEvents.customerReadyReconnect';
    }

    return undefined;
  }

  private normalizeNumber(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return Math.floor(value);
    }
    return null;
  }
}
